/**
 * Theme presets, color palettes, and font pairings.
 * Provides one-click application of curated design combinations.
 * All palette/theme applies use a single undo transaction.
 */

import { store } from '../core/state.js';
import { eventBus } from '../core/events.js';
import { bridge } from '../editing/bridge.js';
import { commitThemeChange, hasOpenTransaction, setThemeVar } from './theme-editor.js';
import { buildFontUpdatePayload, getCurrentHeadingFontWeight } from './font-selector.js';
import { expandPaletteColors } from '../shared/theme-tokens.js';

// ── Color palettes ──
// Each palette defines all 7 CodeStitch theme variables.

export const COLOR_PALETTES = [
  {
    id: 'monochrome',
    name: 'Monochrome',
    colors: {
      '--primary': '#1a1a1a',
      '--primaryLight': '#4a4a4a',
      '--secondary': '#6b7280',
      '--secondaryLight': '#9ca3af',
      '--headerColor': '#111827',
      '--bodyTextColor': '#374151',
      '--bodyTextColorWhite': '#f9fafb'
    }
  },
  {
    id: 'ocean',
    name: 'Ocean',
    colors: {
      '--primary': '#0066cc',
      '--primaryLight': '#3b82f6',
      '--secondary': '#06b6d4',
      '--secondaryLight': '#67e8f9',
      '--headerColor': '#0f172a',
      '--bodyTextColor': '#334155',
      '--bodyTextColorWhite': '#f8fafc'
    }
  },
  {
    id: 'coral',
    name: 'Coral',
    colors: {
      '--primary': '#e85d4a',
      '--primaryLight': '#f09080',
      '--secondary': '#f5a623',
      '--secondaryLight': '#fcd34d',
      '--headerColor': '#1a1a1a',
      '--bodyTextColor': '#4e4b66',
      '--bodyTextColorWhite': '#fafbfc'
    }
  },
  {
    id: 'forest',
    name: 'Forest',
    colors: {
      '--primary': '#166534',
      '--primaryLight': '#22c55e',
      '--secondary': '#65a30d',
      '--secondaryLight': '#bef264',
      '--headerColor': '#14532d',
      '--bodyTextColor': '#3f3f46',
      '--bodyTextColorWhite': '#f0fdf4'
    }
  },
  {
    id: 'sunset',
    name: 'Sunset',
    colors: {
      '--primary': '#ff6a3e',
      '--primaryLight': '#ffba43',
      '--secondary': '#ffba43',
      '--secondaryLight': '#ffd675',
      '--headerColor': '#1a1a1a',
      '--bodyTextColor': '#4e4b66',
      '--bodyTextColorWhite': '#fafbfc'
    }
  },
  {
    id: 'plum',
    name: 'Plum',
    colors: {
      '--primary': '#7c3aed',
      '--primaryLight': '#a78bfa',
      '--secondary': '#ec4899',
      '--secondaryLight': '#f9a8d4',
      '--headerColor': '#1e1b4b',
      '--bodyTextColor': '#4c4869',
      '--bodyTextColorWhite': '#faf5ff'
    }
  },
  {
    id: 'earth',
    name: 'Earth',
    colors: {
      '--primary': '#92400e',
      '--primaryLight': '#d97706',
      '--secondary': '#b45309',
      '--secondaryLight': '#fbbf24',
      '--headerColor': '#1c1917',
      '--bodyTextColor': '#44403c',
      '--bodyTextColorWhite': '#fefce8'
    }
  },
  {
    id: 'slate',
    name: 'Slate',
    colors: {
      '--primary': '#475569',
      '--primaryLight': '#94a3b8',
      '--secondary': '#64748b',
      '--secondaryLight': '#cbd5e1',
      '--headerColor': '#0f172a',
      '--bodyTextColor': '#334155',
      '--bodyTextColorWhite': '#f1f5f9'
    }
  },
  {
    id: 'rose',
    name: 'Rose',
    colors: {
      '--primary': '#e11d48',
      '--primaryLight': '#fb7185',
      '--secondary': '#f43f5e',
      '--secondaryLight': '#fda4af',
      '--headerColor': '#1a1a1a',
      '--bodyTextColor': '#4e4b66',
      '--bodyTextColorWhite': '#fff1f2'
    }
  },
  {
    id: 'teal',
    name: 'Teal',
    colors: {
      '--primary': '#0d9488',
      '--primaryLight': '#2dd4bf',
      '--secondary': '#0891b2',
      '--secondaryLight': '#67e8f9',
      '--headerColor': '#134e4a',
      '--bodyTextColor': '#3f3f46',
      '--bodyTextColorWhite': '#f0fdfa'
    }
  },
  {
    id: 'navy',
    name: 'Navy',
    colors: {
      '--primary': '#1e3a5f',
      '--primaryLight': '#3b82f6',
      '--secondary': '#1e40af',
      '--secondaryLight': '#93c5fd',
      '--headerColor': '#0f172a',
      '--bodyTextColor': '#374151',
      '--bodyTextColorWhite': '#eff6ff'
    }
  },
  {
    id: 'mint',
    name: 'Mint',
    colors: {
      '--primary': '#059669',
      '--primaryLight': '#34d399',
      '--secondary': '#10b981',
      '--secondaryLight': '#a7f3d0',
      '--headerColor': '#064e3b',
      '--bodyTextColor': '#374151',
      '--bodyTextColorWhite': '#ecfdf5'
    }
  }
];

// ── Font pairings ──
// Mirrors Pagy's pairing catalog so themes can draw from the same library.
// Every font referenced here must appear in FONT_OPTIONS (font-selector.js)
// so the loader knows which source to fetch (Google vs Fontshare).

export const FONT_PAIRINGS = [
  // Sans–Sans (same family on heading + body — workhorses)
  { heading: 'Inter', body: 'Inter' },
  { heading: 'General Sans', body: 'General Sans' },
  { heading: 'IBM Plex Sans', body: 'IBM Plex Sans' },
  { heading: 'Geist', body: 'Geist' },
  { heading: 'Satoshi', body: 'Satoshi' },
  { heading: 'Supreme', body: 'Supreme' },
  { heading: 'Instrument Sans', body: 'Instrument Sans' },
  { heading: 'Bricolage Grotesque', body: 'Bricolage Grotesque' },
  { heading: 'Figtree', body: 'Figtree' },
  { heading: 'Source Sans', body: 'Source Sans' },
  { heading: 'Work Sans', body: 'Work Sans' },

  // Sans–Sans (display / body split)
  { heading: 'Poppins', body: 'Inter' },
  { heading: 'Clash Display', body: 'Clash Grotesk' },
  { heading: 'Plein', body: 'Switzer' },
  { heading: 'Bricolage Grotesque', body: 'Manrope' },
  { heading: 'Syne', body: 'Space Grotesk' },
  { heading: 'Pilcrow Rounded', body: 'Archivo' },
  { heading: 'Outfit', body: 'Switzer' },
  { heading: 'Oswald', body: 'Source Sans' },

  // Mono / experimental
  { heading: 'Roboto', body: 'Inconsolata' },
  { heading: 'Inconsolata', body: 'Karla' },
  { heading: 'Iosevka', body: 'Inter' },
  { heading: 'Jet Brains Mono', body: 'Jet Brains Mono' },
  { heading: 'Departure Mono', body: 'iA Writer Mono' },

  // Serif heading + sans body
  { heading: 'Libre Caslon Condensed', body: 'Inter' },
  { heading: 'Instrument Serif', body: 'Instrument Sans' },
  { heading: 'IBM Plex Serif', body: 'IBM Plex Sans' },
  { heading: 'Merriweather', body: 'Merriweather Sans' },
  { heading: 'Fraunces', body: 'Mona Sans' },
  { heading: 'Roboto Slab', body: 'Open Sans' },
  { heading: 'Bespoke Serif', body: 'Bespoke Sans' },
  { heading: 'Alegreya', body: 'Source Sans' },
  { heading: 'Fraunces', body: 'Inter' },
  { heading: 'Boska', body: 'Switzer' },
  { heading: 'Playfair Display', body: 'Libre Franklin' },
  { heading: 'Chubbo', body: 'Supreme' },

  // Sans heading + serif body
  { heading: 'Space Grotesk', body: 'Lora' },
  { heading: 'IBM Plex Sans', body: 'IBM Plex Serif' },
  { heading: 'Source Sans', body: 'Source Serif' },
  { heading: 'Merriweather Sans', body: 'Merriweather' },
  { heading: 'General Sans', body: 'Gambetta' },
  { heading: 'Inter', body: 'Recia' },
  { heading: 'Ranade', body: 'Alegreya' },
  { heading: 'Oswald', body: 'Sentient' },

  // Serif–Serif
  { heading: 'Bitter', body: 'Bitter' },
  { heading: 'Source Serif', body: 'Source Serif' },
  { heading: 'Playfair Display', body: 'Lora' },
  { heading: 'Fraunces', body: 'Alegreya' }
];

// ── Full theme presets ──
// Combine a palette, font pairing, and button style.

export const THEME_PRESETS = [
  {
    id: 'modern-clean',
    name: 'Modern Clean',
    font: 'Inter',
    headingFont: 'Inter',
    paletteId: 'monochrome',
    buttonStyles: { borderRadius: '0.25rem' }
  },
  {
    id: 'bold-coral',
    name: 'Bold Coral',
    font: 'Open Sans',
    headingFont: 'Montserrat',
    paletteId: 'coral',
    buttonStyles: { borderRadius: '3rem' }
  },
  {
    id: 'classic-serif',
    name: 'Classic Serif',
    font: 'Lato',
    headingFont: 'Playfair Display',
    paletteId: 'navy',
    buttonStyles: { borderRadius: '0' }
  },
  {
    id: 'natural',
    name: 'Natural',
    font: 'Nunito',
    headingFont: 'Raleway',
    paletteId: 'forest',
    buttonStyles: { borderRadius: '0.5rem' }
  },
  {
    id: 'warm-sunset',
    name: 'Warm Sunset',
    font: 'Poppins',
    headingFont: 'Libre Baskerville',
    paletteId: 'sunset',
    buttonStyles: { borderRadius: '1rem' }
  },
  {
    id: 'tech-minimal',
    name: 'Tech Minimal',
    font: 'DM Sans',
    headingFont: 'DM Sans',
    paletteId: 'slate',
    buttonStyles: { borderRadius: '0.25rem' }
  },
  {
    id: 'creative-plum',
    name: 'Creative Plum',
    font: 'Work Sans',
    headingFont: 'Josefin Sans',
    paletteId: 'plum',
    buttonStyles: { borderRadius: '3rem' }
  },
  {
    id: 'professional',
    name: 'Professional',
    font: 'Source Sans 3',
    headingFont: 'Merriweather',
    paletteId: 'ocean',
    buttonStyles: { borderRadius: '0.25rem' }
  }
];

// ── Apply functions ──

export function paletteWouldOverwriteCustomColors(
  palette,
  currentTheme = store.getState()?.theme || {}
) {
  if (!palette?.colors || getActivePaletteId(currentTheme)) return false;
  const colors = expandPaletteColors(palette.colors, { existingTheme: currentTheme });
  return Object.entries(colors).some(([key, value]) => {
    const current = String(currentTheme[key] || '').toLowerCase();
    return current && current !== String(value || '').toLowerCase();
  });
}

/**
 * Apply a color palette. All colors update in a single undo step. A caller
 * must explicitly acknowledge overwriting hand-customized tokens.
 */
export function applyPalette(palette, { allowCustomOverwrite = false } = {}) {
  if (!palette?.colors) return false;
  const currentTheme = store.getState()?.theme || {};
  if (!allowCustomOverwrite && paletteWouldOverwriteCustomColors(palette, currentTheme)) {
    return false;
  }
  // Commit any open theme-editor transaction first
  if (hasOpenTransaction()) commitThemeChange();

  store.beginTransaction('Apply color palette');
  for (const [key, val] of Object.entries(
    expandPaletteColors(palette.colors, { existingTheme: currentTheme })
  )) {
    store.update(`theme.${key}`, val, 'Apply palette');
    setThemeVar(key, val);
  }
  store.commitTransaction();
  return true;
}

/**
 * Apply a full theme preset (palette + fonts + button styles) in one undo step.
 */
export function applyThemePreset(preset) {
  if (hasOpenTransaction()) commitThemeChange();

  const palette = COLOR_PALETTES.find((p) => p.id === preset.paletteId);

  store.beginTransaction('Apply theme preset');

  // Colors
  if (palette) {
    const currentTheme = store.getState()?.theme || {};
    for (const [key, val] of Object.entries(
      expandPaletteColors(palette.colors, { existingTheme: currentTheme })
    )) {
      store.update(`theme.${key}`, val, 'Apply theme');
      setThemeVar(key, val);
    }
  }

  // Body font
  store.update('font', preset.font, 'Apply theme');
  bridge.send('update-font', buildFontUpdatePayload(preset.font));

  // Heading font
  if (preset.headingFont) {
    store.update('headingFont', preset.headingFont, 'Apply theme');
    // A real choice — from here the theme outranks the template's own faces.
    store.update('fontSource', 'user', null);
    bridge.send(
      'update-heading-font',
      buildFontUpdatePayload(preset.headingFont, {
        weight: getCurrentHeadingFontWeight()
      })
    );
  }

  // Button styles
  if (preset.buttonStyles) {
    const current = store.getState()?.buttonStyles || {};
    // Applying a theme preset (incl. via the design board) is an explicit user
    // gesture → origin:'user'.
    store.update(
      'buttonStyles',
      { ...current, ...preset.buttonStyles, origin: 'user' },
      'Apply theme'
    );
    // Trigger button CSS rebuild via event
    bridge.send('update-button-styles', {
      css: _buildButtonCSS({ ...current, ...preset.buttonStyles })
    });
  }

  store.commitTransaction();

  // Trigger full button CSS + effect JS rebuild
  if (preset.buttonStyles) {
    eventBus.emit('theme:override-changed');
  }
}

/**
 * Apply a font pairing in one undo step.
 */
export function applyFontPairing(pairing) {
  if (hasOpenTransaction()) commitThemeChange();

  store.beginTransaction('Apply font pairing');
  store.update('font', pairing.body, 'Apply pairing');
  bridge.send('update-font', buildFontUpdatePayload(pairing.body));
  store.update('headingFont', pairing.heading, 'Apply pairing');
  // A real choice — from here the theme outranks the template's own faces.
  store.update('fontSource', 'user', null);
  bridge.send(
    'update-heading-font',
    buildFontUpdatePayload(pairing.heading, {
      weight: getCurrentHeadingFontWeight()
    })
  );
  store.commitTransaction();
}

/**
 * Returns the id of the active palette, or null if none match.
 * Uses exact hex string comparison of all 7 vars.
 */
export function getActivePaletteId(currentTheme) {
  for (const p of COLOR_PALETTES) {
    const match = Object.entries(
      expandPaletteColors(p.colors, { existingTheme: currentTheme })
    ).every(([key, val]) => (currentTheme[key] || '').toLowerCase() === val.toLowerCase());
    if (match) return p.id;
  }
  return null;
}

/**
 * Returns the id of the active theme preset, or null.
 */
export function getActiveThemePresetId(currentTheme, currentFont, currentHeadingFont) {
  for (const t of THEME_PRESETS) {
    const palette = COLOR_PALETTES.find((p) => p.id === t.paletteId);
    if (!palette) continue;
    const colorsMatch = Object.entries(
      expandPaletteColors(palette.colors, { existingTheme: currentTheme })
    ).every(([key, val]) => (currentTheme[key] || '').toLowerCase() === val.toLowerCase());
    if (!colorsMatch) continue;
    if (t.font !== currentFont) continue;
    if (t.headingFont !== (currentHeadingFont || currentFont)) continue;
    return t.id;
  }
  return null;
}

// Minimal button CSS builder for preset application.
// The full version lives in css-assembler.js; this covers the subset
// that theme presets modify (borderRadius only for now). Exported so
// hover-preview snapshot factories (design-widgets.js) can rebuild
// button CSS during revert.
export function _buildButtonCSS(styles) {
  const parts = [];
  const sel = 'main :is(.cs-button-solid, .cs-button-transparent):not(.cs-btn-custom)';
  const rules = [];
  if (styles.borderRadius) rules.push(`border-radius: ${styles.borderRadius}`);
  if (styles.padding) rules.push(`padding: ${styles.padding}`);
  if (styles.backgroundColor) rules.push(`background-color: ${styles.backgroundColor}`);
  if (styles.textColor) rules.push(`color: ${styles.textColor}`);
  if (rules.length) parts.push(`${sel} { ${rules.join('; ')}; }`);
  return parts.join('\n');
}
